import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import {
  ChannelPreviewUIComponentProps,
  useChatContext,
} from 'stream-chat-react';

import { DirectMessagingChannelPreview } from './DirectMessagingChannelPreview';
import { TeamChannelPreview } from './TeamChannelPreview';

import { useWorkspaceController } from '@/contexts/WorkspaceController';

import type { StreamChatType } from '../../types';
import { SupportMessagingChannelPreview } from './SupportMessagingChannelPreview';
import {
  JobSupportChannelPreview,
  type JobSupportChannelStatus,
} from './JobSupportChannelPreview';
import { useJobSupportSession } from '../JobSupportChannelList/JobSupportSessionContext';
import { resolveBookingSessionId } from '../../utils/channelOrdering';

type TeamChannelPreviewProps =
  ChannelPreviewUIComponentProps<StreamChatType> & {
    type: string;
  };

const STATUS_TONES: Record<string, JobSupportChannelStatus['tone']> = {
  error: 'error',
  info: 'info',
  primary: 'info',
  success: 'success',
  warning: 'warning',
};

const getRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const getMetadataValue = (
  metadata: Record<string, unknown>,
  keys: string[]
) => {
  for (const key of keys) {
    const value = metadata[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return value;
    }
  }

  return null;
};

const getChannelMetadata = (
  channel: TeamChannelPreviewProps['channel']
): Record<string, unknown> => {
  const channelData = getRecord(channel.data);
  return getRecord(
    channelData.channelMetaData ??
      channelData.channelMetadata ??
      channelData.channel_meta_data
  );
};

const normalizeJobSupportStatus = (
  value: unknown
): JobSupportChannelStatus | null => {
  const statusRecord = getRecord(value);
  const rawStyleClass =
    statusRecord.style_class ?? statusRecord.styleClass ?? statusRecord.tone;
  const tone = STATUS_TONES[String(rawStyleClass ?? '').toLowerCase()];

  if (!tone) return null;

  const display = String(
    statusRecord.display ??
      statusRecord.name ??
      statusRecord.label ??
      statusRecord.value ??
      'Status'
  ).trim();

  return { display: display || 'Status', tone };
};

const getMetadataBookingSessionStatus = (
  metadata: Record<string, unknown>
): unknown =>
  getMetadataValue(metadata, [
    'bookingSessionStatus',
    'booking_session_status',
    'sessionStatus',
    'session_status',
    'bookingStatus',
    'booking_status',
    'status',
  ]);

export const ChannelPreview = ({ channel, type }: TeamChannelPreviewProps) => {
  const { channel: activeChannel, setActiveChannel } =
    useChatContext<StreamChatType>();
  const { displayWorkspace } = useWorkspaceController();
  const jobSupportSessionId = useMemo(
    () => (type === 'jobSupport' ? resolveBookingSessionId(channel) : null),
    [channel, type]
  );
  const jobSupportSession = useJobSupportSession(jobSupportSessionId);
  const jobSupportStatus = useMemo(() => {
    if (type !== 'jobSupport') return null;

    const metadataStatus = normalizeJobSupportStatus(
      getMetadataBookingSessionStatus(getChannelMetadata(channel))
    );

    return (
      metadataStatus ??
      normalizeJobSupportStatus(jobSupportSession?.booking_session_status)
    );
  }, [channel, jobSupportSession, type]);

  const handleClick = useCallback(() => {
    displayWorkspace('Chat');
    if (setActiveChannel) {
      setActiveChannel(channel);
    }
  }, [channel, displayWorkspace, setActiveChannel]);

  const unreadCount = channel.countUnread();

  return (
    <button
      className={clsx('channel-preview', {
        selected: channel?.id === activeChannel?.id,
      })}
      onClick={handleClick}
    >
      {type === 'team' && (
        <TeamChannelPreview
          name={
            channel?.data?.name || (channel?.data?.id as string) || 'random'
          }
          date=""
          unreadCount={unreadCount}
        />
      )}
      {type === 'jobSupport' && (
        <JobSupportChannelPreview
          name={
            channel?.data?.name || (channel?.data?.id as string) || 'random'
          }
          date={(channel?.data?.channelMetaData as any)?.bookingDate}
          status={jobSupportStatus}
          time={(channel?.data?.channelMetaData as any)?.bookingTime}
          unreadCount={unreadCount}
        />
      )}
      {type === 'messaging' && (
        <DirectMessagingChannelPreview
          channel={channel}
          unreadCount={unreadCount}
        />
      )}
      {(type === 'cleanerSupport' || type === 'clientSupport') && (
        <SupportMessagingChannelPreview
          channel={channel}
          unreadCount={unreadCount}
        />
      )}
    </button>
  );
};
